import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Container,
  Paper,
  TextField,
  Typography,
  Checkbox,
  FormControlLabel,
  IconButton,
  Grid,
  Chip,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import InstagramIcon from "@mui/icons-material/Instagram";
import { useApi } from "../../utils/api";
import { useUserInfo } from "../../utils/user";
import Loading from "../Layout/Loading";

const CoachProfileAdmin = () => {
  const { getCoachProfileMe, saveCoachProfile, getProducts, putSignedUrl } =
    useApi();
  const { id: coachId, isAccountFullySetup } = useUserInfo();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState({
    slug: "",
    displayName: "",
    bio: "",
    imageUrl: "",
    bannerImageUrl: "",
    galleryImages: [],
    instagramUsername: "",
    instagramPhotos: [],
    publicProducts: [],
  });
  const [products, setProducts] = useState([]);
  const [imageUploading, setImageUploading] = useState(false);
  const [bannerUploading, setBannerUploading] = useState(false);
  const [galleryUploading, setGalleryUploading] = useState(false);
  const [fetchingInstagram, setFetchingInstagram] = useState(false);

  const fetchProfile = async () => {
    setLoading(true);
    try {
      const p = (await getCoachProfileMe()) || {};
      setProfile({
        slug: p.slug || "",
        displayName: p.displayName || "",
        bio: p.bio || "",
        imageUrl: p.imageUrl || "",
        bannerImageUrl: p.bannerImageUrl || "",
        galleryImages: p.galleryImages || [],
        instagramUsername: p.instagramUsername || "",
        instagramPhotos: p.instagramPhotos || [],
        publicProducts: (p.publicProducts || []).filter(Boolean),
      });
      if (isAccountFullySetup) {
        const prod = (await getProducts()) || [];
        setProducts(prod);
      } else {
        setProducts([]);
      }
    } catch (err) {
      console.error(err);
      alert("Failed to load profile");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProfile();
  }, []);

  const handleImageUpload = async (file) => {
    if (!file) return;
    try {
      setImageUploading(true);
      const ext = file.name.split(".").pop() || "jpg";
      const key = `public/${coachId}/coach-profile-${Date.now()}.${ext}`;
      const { uploadUrl } = (await putSignedUrl(key)) || {};
      const publicUrl = uploadUrl ? uploadUrl.split("?")[0] : null;
      if (!uploadUrl || !publicUrl) {
        throw new Error("No upload URL returned for image");
      }

      await fetch(uploadUrl, {
        method: "PUT",
        headers: {
          "Content-Type": file.type,
        },
        body: file,
      });

      setProfile((prev) => ({ ...prev, imageUrl: publicUrl }));
    } catch (err) {
      console.error(err);
      alert("Image upload failed");
    } finally {
      setImageUploading(false);
    }
  };

  const handleBannerUpload = async (file) => {
    if (!file) return;
    try {
      setBannerUploading(true);
      const ext = file.name.split(".").pop() || "jpg";
      const key = `public/${coachId}/coach-banner-${Date.now()}.${ext}`;
      const { uploadUrl } = (await putSignedUrl(key)) || {};
      const publicUrl = uploadUrl ? uploadUrl.split("?")[0] : null;
      if (!uploadUrl || !publicUrl) {
        throw new Error("No upload URL returned for image");
      }

      await fetch(uploadUrl, {
        method: "PUT",
        headers: {
          "Content-Type": file.type,
        },
        body: file,
      });

      setProfile((prev) => ({ ...prev, bannerImageUrl: publicUrl }));
    } catch (err) {
      console.error(err);
      alert("Banner upload failed");
    } finally {
      setBannerUploading(false);
    }
  };

  const handleGalleryUpload = async (files) => {
    if (!files || files.length === 0) return;
    try {
      setGalleryUploading(true);
      const uploadPromises = Array.from(files).map(async (file) => {
        const ext = file.name.split(".").pop() || "jpg";
        const key = `public/${coachId}/coach-gallery-${Date.now()}-${Math.random()
          .toString(36)
          .substring(7)}.${ext}`;
        const { uploadUrl } = (await putSignedUrl(key)) || {};
        const publicUrl = uploadUrl ? uploadUrl.split("?")[0] : null;
        if (!uploadUrl || !publicUrl) {
          throw new Error("No upload URL returned for image");
        }

        await fetch(uploadUrl, {
          method: "PUT",
          headers: {
            "Content-Type": file.type,
          },
          body: file,
        });

        return publicUrl;
      });

      const uploadedUrls = await Promise.all(uploadPromises);
      setProfile((prev) => ({
        ...prev,
        galleryImages: [...(prev.galleryImages || []), ...uploadedUrls],
      }));
    } catch (err) {
      console.error(err);
      alert("Gallery upload failed");
    } finally {
      setGalleryUploading(false);
    }
  };

  const removeGalleryImage = (index) => {
    setProfile((prev) => ({
      ...prev,
      galleryImages: prev.galleryImages.filter((_, i) => i !== index),
    }));
  };

  const fetchInstagramPhotos = async () => {
    if (!profile.instagramUsername) {
      alert("Please enter an Instagram username first");
      return;
    }
    try {
      setFetchingInstagram(true);
      // Note: Instagram Basic Display API requires OAuth setup
      // For now, this is a placeholder that can be enhanced with actual API integration
      // You would need to set up Instagram OAuth and use their API
      alert(
        "Instagram integration requires OAuth setup. This feature will be available soon."
      );
      // Placeholder: In a real implementation, you would:
      // 1. Use Instagram Basic Display API or Graph API
      // 2. Fetch user's media
      // 3. Store photo URLs in instagramPhotos array
      // setProfile((prev) => ({ ...prev, instagramPhotos: fetchedPhotos }));
    } catch (err) {
      console.error(err);
      alert("Failed to fetch Instagram photos");
    } finally {
      setFetchingInstagram(false);
    }
  };

  const toggleProduct = (priceId) => {
    if (!priceId) return;
    setProfile((prev) => {
      const exists = prev.publicProducts.includes(priceId);
      const next = exists
        ? prev.publicProducts.filter((id) => id !== priceId)
        : [...prev.publicProducts, priceId];
      return { ...prev, publicProducts: next };
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await saveCoachProfile({
        slug: profile.slug,
        displayName: profile.displayName,
        bio: profile.bio,
        imageUrl: profile.imageUrl,
        bannerImageUrl: profile.bannerImageUrl,
        galleryImages: profile.galleryImages || [],
        instagramUsername: profile.instagramUsername,
        instagramPhotos: profile.instagramPhotos || [],
        publicProducts: (profile.publicProducts || []).filter(Boolean),
      });
      await fetchProfile();
    } catch (err) {
      console.error(err);
      alert(
        err?.message?.includes("Slug")
          ? "Slug already in use. Pick another."
          : "Failed to save profile"
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Container sx={{ mt: 10 }}>
        <Loading />
      </Container>
    );
  }

  return (
    <Container sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Public Coach Profile
        </Typography>
        <TextField
          label="Slug (trainer-app.com/coaches/slug)"
          fullWidth
          size="small"
          value={profile.slug}
          onChange={(e) =>
            setProfile((prev) => ({ ...prev, slug: e.target.value }))
          }
          sx={{ mb: 2 }}
        />
        <TextField
          label="Display Name"
          fullWidth
          size="small"
          value={profile.displayName}
          onChange={(e) =>
            setProfile((prev) => ({ ...prev, displayName: e.target.value }))
          }
          sx={{ mb: 2 }}
        />
        <TextField
          label="Bio"
          fullWidth
          size="small"
          multiline
          rows={4}
          value={profile.bio}
          onChange={(e) =>
            setProfile((prev) => ({ ...prev, bio: e.target.value }))
          }
          sx={{ mb: 2 }}
        />
        <Typography variant="subtitle1" sx={{ mb: 1, mt: 2 }}>
          Profile Image
        </Typography>
        <Box sx={{ mb: 3, display: "flex", gap: 2, alignItems: "center" }}>
          {profile.imageUrl && (
            <img
              src={profile.imageUrl}
              alt="Coach"
              style={{
                width: 120,
                height: 120,
                objectFit: "cover",
                borderRadius: 8,
              }}
            />
          )}
          <Button
            variant="outlined"
            component="label"
            disabled={imageUploading}
          >
            {imageUploading ? "Uploading..." : "Upload Profile Image"}
            <input
              type="file"
              hidden
              accept="image/*"
              onChange={(e) => handleImageUpload(e.target.files?.[0])}
            />
          </Button>
        </Box>

        <Typography variant="subtitle1" sx={{ mb: 1 }}>
          Banner Image
        </Typography>
        <Box sx={{ mb: 3 }}>
          {profile.bannerImageUrl && (
            <Box sx={{ mb: 2, position: "relative" }}>
              <img
                src={profile.bannerImageUrl}
                alt="Banner"
                style={{
                  width: "100%",
                  maxHeight: 300,
                  objectFit: "cover",
                  borderRadius: 8,
                }}
              />
              <IconButton
                onClick={() =>
                  setProfile((prev) => ({ ...prev, bannerImageUrl: "" }))
                }
                sx={{
                  position: "absolute",
                  top: 8,
                  right: 8,
                  bgcolor: "rgba(0,0,0,0.5)",
                  color: "white",
                  "&:hover": { bgcolor: "rgba(0,0,0,0.7)" },
                }}
              >
                <DeleteIcon />
              </IconButton>
            </Box>
          )}
          <Button
            variant="outlined"
            component="label"
            disabled={bannerUploading}
            startIcon={<AddPhotoAlternateIcon />}
          >
            {bannerUploading ? "Uploading..." : "Upload Banner"}
            <input
              type="file"
              hidden
              accept="image/*"
              onChange={(e) => handleBannerUpload(e.target.files?.[0])}
            />
          </Button>
        </Box>

        <Typography variant="subtitle1" sx={{ mb: 1 }}>
          Gallery Images
        </Typography>
        <Box sx={{ mb: 3 }}>
          {profile.galleryImages && profile.galleryImages.length > 0 && (
            <Grid container spacing={2} sx={{ mb: 2 }}>
              {profile.galleryImages.map((url, index) => (
                <Grid item xs={6} sm={4} md={3} key={index}>
                  <Box sx={{ position: "relative" }}>
                    <img
                      src={url}
                      alt={`Gallery ${index + 1}`}
                      style={{
                        width: "100%",
                        height: 150,
                        objectFit: "cover",
                        borderRadius: 8,
                      }}
                    />
                    <IconButton
                      onClick={() => removeGalleryImage(index)}
                      sx={{
                        position: "absolute",
                        top: 4,
                        right: 4,
                        bgcolor: "rgba(0,0,0,0.5)",
                        color: "white",
                        "&:hover": { bgcolor: "rgba(0,0,0,0.7)" },
                        width: 32,
                        height: 32,
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Grid>
              ))}
            </Grid>
          )}
          <Button
            variant="outlined"
            component="label"
            disabled={galleryUploading}
            startIcon={<AddPhotoAlternateIcon />}
          >
            {galleryUploading ? "Uploading..." : "Add Gallery Images"}
            <input
              type="file"
              hidden
              accept="image/*"
              multiple
              onChange={(e) => handleGalleryUpload(e.target.files)}
            />
          </Button>
        </Box>

        <Typography variant="subtitle1" sx={{ mb: 1 }}>
          Instagram Integration
        </Typography>
        <Box sx={{ mb: 3, display: "flex", gap: 2, alignItems: "center" }}>
          <TextField
            label="Instagram Username"
            size="small"
            value={profile.instagramUsername}
            onChange={(e) =>
              setProfile((prev) => ({
                ...prev,
                instagramUsername: e.target.value,
              }))
            }
            placeholder="@username"
            sx={{ flex: 1 }}
          />
          <Button
            variant="outlined"
            startIcon={<InstagramIcon />}
            onClick={fetchInstagramPhotos}
            disabled={fetchingInstagram || !profile.instagramUsername}
          >
            {fetchingInstagram ? "Fetching..." : "Fetch Photos"}
          </Button>
        </Box>
        {profile.instagramPhotos && profile.instagramPhotos.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {profile.instagramPhotos.length} Instagram photos loaded
            </Typography>
            <Grid container spacing={2}>
              {profile.instagramPhotos.map((photo, index) => (
                <Grid item xs={6} sm={4} md={3} key={index}>
                  <img
                    src={photo.url || photo}
                    alt={`Instagram ${index + 1}`}
                    style={{
                      width: "100%",
                      height: 150,
                      objectFit: "cover",
                      borderRadius: 8,
                    }}
                  />
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        <Typography variant="subtitle1" sx={{ mb: 1 }}>
          Public Products
        </Typography>
        {isAccountFullySetup ? (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 2 }}>
            {products.map((p) => {
              const prices = Array.isArray(p.prices) ? p.prices : [];
              // If no prices, fall back to single id
              if (!prices.length) {
                const priceId = p.priceId || p.id || p.productId;
                const labelName = p.name || p.productId || p.id || "Unnamed";
                return (
                  <FormControlLabel
                    key={priceId || p.id}
                    control={
                      <Checkbox
                        checked={
                          priceId
                            ? profile.publicProducts.includes(priceId)
                            : false
                        }
                        disabled={!priceId}
                        onChange={() => toggleProduct(priceId)}
                      />
                    }
                    label={`${labelName}${
                      p.description ? ` - ${p.description}` : ""
                    }`}
                  />
                );
              }

              return prices.map((price) => {
                const priceId = price.priceId;
                const labelName = p.name || p.productId || p.id || "Unnamed";
                const dollars =
                  price.unitAmount !== undefined && price.unitAmount !== null
                    ? `$${(price.unitAmount / 100).toFixed(2)}`
                    : "";
                const nickname = price.nickname || price.description || "";
                const priceLabel = [nickname, dollars]
                  .filter(Boolean)
                  .join(" ");
                return (
                  <FormControlLabel
                    key={`${p.id || p.productId}-${priceId}`}
                    control={
                      <Checkbox
                        checked={
                          priceId
                            ? profile.publicProducts.includes(priceId)
                            : false
                        }
                        disabled={!priceId}
                        onChange={() => toggleProduct(priceId)}
                      />
                    }
                    label={`${labelName}${
                      priceLabel ? ` - ${priceLabel}` : ""
                    }`}
                  />
                );
              });
            })}
            {!products.length && (
              <Typography color="text.secondary" variant="body2">
                No products available. Create products first.
              </Typography>
            )}
          </Box>
        ) : (
          <Typography color="text.secondary" variant="body2">
            Account not fully setup. Please complete the setup process before
            adding products.
          </Typography>
        )}

        <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            Save
          </Button>
          <Button onClick={fetchProfile} disabled={saving}>
            Reset
          </Button>
        </Box>
      </Paper>
    </Container>
  );
};

export default CoachProfileAdmin;
